/**
 * Module dependencies.
 */

import { Collapse } from 'src/components/core/collapse';
import { FAQPageJsonLd } from 'next-seo';
import { FadeInUp } from 'src/components/core/animations/fade-in-up';
import { FaqProps } from 'src/prismic/utils';
import { PrismicRichText, PrismicRichTextWrapper } from 'src/components/prismic';
import { ReactNode, useState } from 'react';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  faqs: FaqProps[];
  isLight?: boolean;
  staggerMobile?: boolean;
  triggerInView?: boolean;
};

/**
 * `FaqComponentProps` type.
 */

type FaqComponentProps = {
  content: ReactNode;
  isLight?: boolean;
  title: string;
};

/**
 * `CollapseWrapper` styled component.
 */

const CollapseWrapper = styled.div`
  display: grid;
  grid-gap: var(--space-xxs);
  padding-top: var(--space-xs);
`;

/**
 * `Faq` component.
 */

const Faq = ({ content, isLight, title }: FaqComponentProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Collapse isExpanded={isExpanded} isLight={isLight} onClick={() => setIsExpanded(value => !value)} title={title}>
      {content}
    </Collapse>
  );
};

/**
 * Export `Faqs` component.
 */

export const Faqs = ({ faqs, isLight, staggerMobile, triggerInView }: Props) => (
  <>
    <FAQPageJsonLd
      mainEntity={faqs?.map(({ answer, question }) => ({
        acceptedAnswerText: answer,
        questionName: question
      }))}
    />

    <CollapseWrapper>
      {faqs?.map(({ answer, question }, index) => (
        <FadeInUp key={index} staggerMobile={staggerMobile} triggerInView={triggerInView}>
          <Faq
            content={
              <PrismicRichTextWrapper>
                <PrismicRichText field={answer} />
              </PrismicRichTextWrapper>
            }
            isLight={isLight}
            title={question as string}
          />
        </FadeInUp>
      ))}
    </CollapseWrapper>
  </>
);
